/* LOCKT product page helpers: carousels, stock counter, ship date. Loaded as a module (runs once). */

/**
 * <lk-carousel> wraps a horizontal scroll-snap track:
 *   [data-lk-track]  the scroller (direct children = slides)
 *   [data-lk-prev] / [data-lk-next]  optional buttons
 *   [data-lk-dots]   optional dots container (filled by JS)
 *   [data-lk-nav]    optional nav wrapper, hidden when nothing overflows
 */
class LkCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lk-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-lk-prev]');
    this.next = this.querySelector('[data-lk-next]');
    this.dotsWrap = this.querySelector('[data-lk-dots]');
    this.nav = this.querySelector('[data-lk-nav]');
    this.slides = Array.from(this.track.children);

    this.onScroll = () => {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.update();
      });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));

    if (this.dotsWrap) {
      this.dotsWrap.innerHTML = '';
      this.dots = this.slides.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', `Slide ${i + 1}`);
        b.addEventListener('click', () => this.scrollToIndex(i));
        this.dotsWrap.appendChild(b);
        return b;
      });
    }

    this.ro = new ResizeObserver(() => this.update());
    this.ro.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.ro?.disconnect();
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  /** Scroll position at which a slide is snapped to the start of the track. */
  pos(slide) {
    const pad = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    return slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft - pad;
  }

  index() {
    const left = this.track.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    this.slides.forEach((s, i) => {
      const d = Math.abs(this.pos(s) - left);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  }

  scrollToIndex(i) {
    const s = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
    if (!s) return;
    this.track.scrollTo({ left: this.pos(s), behavior: 'smooth' });
  }

  go(dir) {
    this.scrollToIndex(this.index() + dir);
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const overflow = max > 4;
    if (this.nav) this.nav.hidden = !overflow;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 4;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max - 4;
    if (this.dots) {
      const i = this.track.scrollLeft >= max - 4 ? this.slides.length - 1 : this.index();
      this.dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
    }
  }
}

/** <lk-stock-count data-min="11" data-max="21">15</lk-stock-count> : random odd number, like the old page. */
class LkStockCount extends HTMLElement {
  connectedCallback() {
    const min = parseInt(this.dataset.min, 10) || 11;
    const max = Math.max(min, parseInt(this.dataset.max, 10) || 21);
    let n = Math.floor(Math.random() * (max - min + 1)) + min;
    if (n % 2 === 0) n = n + 1 > max ? n - 1 : n + 1;
    this.textContent = String(n);
  }
}

/** <lk-ship-date data-offset="1" data-skip-weekends="true">Thu, Oct 15</lk-ship-date> */
class LkShipDate extends HTMLElement {
  connectedCallback() {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const d = new Date();
    d.setDate(d.getDate() + (parseInt(this.dataset.offset, 10) || 0));
    if (this.dataset.skipWeekends === 'true') {
      if (d.getDay() === 6) d.setDate(d.getDate() + 2);
      else if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    }
    this.textContent = `${days[d.getDay()]}, ${months[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}`;
  }
}

if (!customElements.get('lk-carousel')) customElements.define('lk-carousel', LkCarousel);
if (!customElements.get('lk-stock-count')) customElements.define('lk-stock-count', LkStockCount);
if (!customElements.get('lk-ship-date')) customElements.define('lk-ship-date', LkShipDate);
